import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useNavigate, useLocation } from "react-router-dom";
import SharedButton from "../../components/Shared/SharedButton";
import CustomSearchDropdown from "../../components/Shared/CustomSearchDropdown";
import SwitchInput from "../../components/Shared/SwitchInput";
import {
  createAccounts,
  getAccounts,
  getListAccounts,
  getSubTypesDropdown,
  getTypesDropdown,
  updateAccount,
} from "../../store/slices/AcountsSlice";
import { getBranches, GetBusinesses } from "../../store/slices/BusinessSlice";
import TextInput from "../../components/Shared/TextInput";
import DropdownComponent from "../../components/Shared/DropdownComponent";
import { FaArrowCircleLeft } from "react-icons/fa";

export default function CreateAccountPage() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = i18next.language;
  const navigate = useNavigate();
  const location = useLocation();
  const updateData = location?.state?.updateData || null;
  const parentAccountData = location?.state?.parentAccountData || null;
  const { user } = useSelector((state) => state.auth);
  const { listAccounts, coaTypes, coaTypesBySubTypes } = useSelector(
    (state) => state.acount,
  );
  const { business, branches } = useSelector((state) => state.business);
  const { isLoading } = useSelector((state) => state.acount);

  const listAccountsvalues = useMemo(
    () =>
      listAccounts?.data
        ? Object.entries(listAccounts.data).map(([id, account]) => ({
          id: Number(account?.code),
          name: lang === "ar" ? account.name_ar : account.name_en,
          name_en: account.name_en,
          name_ar: account.name_ar,
        }))
        : [],
    [listAccounts, lang],
  );

  const coaTypesValues = useMemo(
    () =>
      coaTypes?.data
        ? coaTypes.data.map((type) => ({
          id: Number(type.id),
          name: lang === "ar" ? type.name_ar : type.name_en,
          name_en: type.name_en,
          name_ar: type.name_ar,
        }))
        : [],
    [coaTypes, lang],
  );

  useEffect(() => {
    dispatch(getTypesDropdown());
    dispatch(GetBusinesses());
    dispatch(getBranches());
    if (updateData || parentAccountData) dispatch(getListAccounts());
  }, [dispatch, updateData, parentAccountData]);

  useEffect(() => {
    if (updateData?.sub_type_en) {
      const typeId = coaTypesValues.find(
        (typ) => typ?.name_en === updateData?.type_en,
      )?.id;
      if (typeId) dispatch(getSubTypesDropdown(typeId));
    }
  }, [dispatch, updateData, coaTypesValues]);

  const subTypesOptions = useMemo(
    () =>
      (coaTypesBySubTypes || []).map((subType) => ({
        id: Number(subType.id),
        name: lang === "ar" ? subType.name_ar : subType.name_en,
        name_en: subType.name_en,
        name_ar: subType.name_ar,
      })),
    [coaTypesBySubTypes, lang],
  );

  const initialValues = {
    code: updateData ? updateData?.code : "",
    name_en: updateData ? updateData?.name_en : "",
    name_ar: updateData ? updateData?.name_ar : "",
    type: updateData
      ? coaTypesValues.find((typ) => typ?.name_en === updateData?.type_en)?.id
      : "",
    sub_type: updateData
      ? (subTypesOptions || []).find(
        (typ) => typ?.name_en === updateData?.sub_type_en,
      )?.id || ""
      : "",
    balance: updateData ? updateData?.balance : "",
    branch_id: updateData ? updateData?.branch_id : user?.branch_id,
    business_id: updateData ? updateData?.business_id : user?.business_id,
    category: updateData
      ? updateData?.category
      : parentAccountData
        ? "child"
        : "",
    parent_code: updateData
      ? updateData?.parent_code
      : parentAccountData?.data?.code || parentAccountData?.code || 0,
    description: updateData ? updateData?.description : "",
    is_vat_in: updateData ? updateData.is_vat_in : false,
    is_vat_out: updateData ? updateData.is_vat_out : false,
    is_head: updateData ? updateData.is_head : false,
    code: updateData ? updateData?.code : "",
  };

  const validationSchema = yup.object().shape({
    code: updateData
      ? yup.number().required(t("code_is_required"))
      : yup.number(),
    name_en: yup.string().required(t("name_is_required")),
    name_ar: yup.string().required(t("name_ar_is_required")),
    type: yup.string().required(t("type_is_required")),
    sub_type: yup.string().required(t("sub_type_is_required")),
    balance: yup.number().typeError(t("Must be a number")),
    category: yup.string().required(t("category_is_required")),
    parent_code: yup.number(),
  });

  const handleSubmit = async (values, { resetForm }) => {
    const typeDetails = coaTypesValues.find((type) => type.id === values.type);
    const subTypeDetails = (coaTypesBySubTypes || []).find(
      (st) => st.id === values.sub_type,
    );

    const formData = {
      ...values,
      balance: Number(values.balance) || 0,
      type_en: typeDetails ? typeDetails.name_en : "",
      type_ar: typeDetails ? typeDetails.name_ar : "",
      sub_type_en: subTypeDetails ? subTypeDetails.name_en : "",
      sub_type_ar: subTypeDetails ? subTypeDetails.name_ar : "",
    };

    if (updateData) {
      formData.code = values.code ? Number(values.code) : 0;
      const res = await dispatch(
        updateAccount({ id: updateData?.id, data: formData }),
      );
      if (updateAccount.fulfilled.match(res)) {
        resetForm();
        navigate("/manager/accounts");
        dispatch(getAccounts({ account_type: updateData?.type_en || "" }));
      }
    } else {
      if (!values.code) {
        delete formData.code;
      } else {
        formData.code = Number(values.code);
      }
      const res = await dispatch(createAccounts(formData));
      if (createAccounts.fulfilled.match(res)) {
        resetForm();
        navigate("/manager/accounts");
      }
    }
  };

  return (
    <div className="flex flex-col rounded-md w-full gap-4 lg:max-h-[calc(100vh-150px)]">
      <div className="flex items-center mb-2">
        <FaArrowCircleLeft
          className="text-primary cursor-pointer me-2"
          size={20}
          onClick={() => navigate("/manager/accounts")}
        />
        <h2 className="text-[16px] text-darkBg font-[600] lg:text-[20px] md:text-[18px]">
          {t(updateData ? "Update Account" : "Create Account")}
        </h2>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 w-full">
              <TextInput
                label={t("Account Code")}
                name="code"
                type="number"
                placeholder={t("Account Code")}
              />

              <TextInput
                label={t("name_en")}
                name="name_en"
                placeholder={t("name_en")}
              />

              <TextInput
                label={t("name_ar")}
                name="name_ar"
                placeholder={t("name_ar")}
              />

              <DropdownComponent
                label={t("type")}
                name="type"
                options={coaTypesValues}
                optionLabel="name"
                optionValue="id"
                placeholder={`${t("select")} ${t("type")}`}
                onChange={(e) => {
                  formik.setFieldValue("type", e.value);
                  dispatch(getSubTypesDropdown(e.value));
                }}
              />

              <DropdownComponent
                label={t("sub_type")}
                name="sub_type"
                options={subTypesOptions}
                optionLabel="name"
                optionValue="id"
                placeholder={`${t("select")} ${t("subType")}`}
              />

              <TextInput
                label={t("balance")}
                name="balance"
                type="text"
                inputMode="decimal"
                onChange={(e) => {
                  const { value } = e.target;

                  if (/^-?\d*\.?\d*$/.test(value)) {
                    formik.setFieldValue("balance", value);
                  }
                }}
                placeholder={`${t("enter")} ${t("balance")}`}
              />

              <DropdownComponent
                label={t("Insurance Company")}
                name="insurance_company_reference"
                options={business?.data?.map((bl) => ({
                  label: bl.name_en,
                  value: bl.license_id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={`${t("select")} ${t("Insurance Company")}`}
              />

              <DropdownComponent
                label={t("Branch")}
                name="branch_id"
                options={branches?.data?.map((bl) => ({
                  label: bl.name_en,
                  value: bl.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={`${t("select")} ${t("Branch")}`}
              />

              <DropdownComponent
                label={t("category")}
                name="category"
                options={[{ value: "child", label: t("child") }]}
                optionLabel="label"
                optionValue="value"
                disabled={updateData}
                placeholder={`${t("select")} ${t("category")}`}
                onChange={(e) => {
                  formik.setFieldValue("category", e.value);

                  if (e.value === "child") {
                    dispatch(getListAccounts());
                  }
                }}
              />

              {formik.values.category === "child" &&
                listAccountsvalues.length > 0 && (
                  <CustomSearchDropdown
                    label={t("account")}
                    name={"parent_code"}
                    disabled={updateData}
                    options={listAccountsvalues}
                    placeholder={`${t("select")}${t("account")}`}
                    value={formik.values.parent_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={"!w-full !h-[30px] !bg-gray-100"}
                  />
                )}

              <TextInput
                label={t("description")}
                name="description"
                placeholder={t("description")}
              />
              <div className={`flex items-start gap-3 mt-2 ${lang == 'ar' ? 'col-span-2' : ''}`}>
                <SwitchInput
                  label={t("is_vat_in")}
                  value={formik.values.is_vat_in}
                  handleChange={(e) => {
                    formik.setFieldValue("is_vat_in", e.value);
                    if (e.value) {
                      formik.setFieldValue("is_vat_out", false);
                    }
                  }}
                />
                <SwitchInput
                  label={t("is_vat_out")}
                  value={formik.values.is_vat_out}
                  handleChange={(e) => {
                    formik.setFieldValue("is_vat_out", e.value);
                    if (e.value) {
                      formik.setFieldValue("is_vat_in", false);
                    }
                  }}
                />
                <SwitchInput
                  label={t("is_head")}
                  value={formik.values.is_head}
                  handleChange={(e) => formik.setFieldValue("is_head", e.value)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <SharedButton
                type="button"
                icon="pi pi-times"
                label={t("Cancel")}
                buttonType="reset"
                onClick={() => navigate("/manager/accounts")}
              />
              <SharedButton
                type="submit"
                icon="pi pi-check"
                loading={isLoading}
                disabled={isLoading}
                label={t(updateData ? "Update" : "Submit")}
                className="submit-btn"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
